import { Fragment, useEffect, useMemo, useState } from "react";
import { MaterialReactTable, MRT_Row, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import dayjs from 'dayjs';
import { Badge, Box, Button, Grid, IconButton, Link, ListItemIcon, Menu, MenuItem, MenuProps, Paper, Tooltip, Typography, alpha, capitalize, styled } from '@mui/material';
import assets from "../../../assets";
import { useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CssBaseline from '@mui/material/CssBaseline';
import { AuditSessionStorageKey, AuditSessionStorageModel, AuditTableParameterStorageService } from "../../../services/sessionStorage";
import RefreshIcon from '@mui/icons-material/Refresh';
import colorConfigs from "../../../configs/colorConfigs";
import { Link as RouterLink } from 'react-router-dom';
import RotateRightOutlinedIcon from '@mui/icons-material/RotateRightOutlined';
import { tsXLXS } from 'ts-xlsx-export';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import DomainAddOutlinedIcon from '@mui/icons-material/DomainAddOutlined';
import { MRT_FilterFns } from 'material-react-table';
import { useMediaQuery } from '@mui/material';
import RareButton from "../buttons/RareButton";
import {
  MRT_ToggleGlobalFilterButton,
  MRT_ToggleFiltersButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFullScreenButton,
} from 'material-react-table';

const StyledMenu = styled((props: any) => (
  <Menu data-testid="Menu-e9et"
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0',
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));


interface AuditTableProps {
  rows: any[];
  isLoading: boolean;
  sorting: any;
  columnVisibility: any;
  columnFilters: any;
  pagination: any;
  globalFilter: any;
  showGlobalFilter: any;
  setShowGlobalFilter: (value: any) => void;
  handleRemoveAllFilters: () => void;
  setGlobalFilter: (value: string) => void;
  setSorting: (sorting: any[]) => void;
  setColumnVisibility: (columnVisibility: { [key: string]: boolean }) => void;
  setColumnFilters: (filters: any[]) => void;
  setPagination: (pagination: { pageSize: number; pageIndex: number }) => void;
  setAudit: (detail: any) => void;
  setPopup: (detail: any) => void;
  handleRefreshButton: () => void;
  openEditPage: (detail: any) => void;
  openCreatePage: () => void;
}

const AuditsTable: React.FC<AuditTableProps> = ({
  rows,
  isLoading,
  sorting,
  columnVisibility,
  columnFilters,
  pagination,
  globalFilter,
  showGlobalFilter,
  setShowGlobalFilter,
  setGlobalFilter,
  handleRemoveAllFilters,
  setSorting,
  setColumnVisibility,
  setColumnFilters,
  setPagination,
  setAudit,
  setPopup,
  handleRefreshButton,
  openEditPage,
  openCreatePage,
}) => {

  console.log("rows", rows)
  const isMobile = useMediaQuery('(max-width:768px)');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleSaveFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const customStatusFilter = (row: any, columnId: any, filterValue: any) => {
    const normalize = (str: string) => str.toLowerCase().replace(/_/g, ' ').trim(); // Convert to lowercase, replace underscores with spaces, and trim
    const status = normalize(row.getValue(columnId)?.toString() || ""); // Normalize the status value

    // Normalize filterValue to handle both single strings and arrays
    const normalizedFilterValues = Array.isArray(filterValue)
      ? filterValue.map((value) => normalize(value))
      : [normalize(filterValue)];
    // Check if any of the normalized filter values are partially included in the status
    return normalizedFilterValues.some((value) => status.includes(value));
  };

  const filterFns = {
    ...MRT_FilterFns,
    customStatusFilter, // Add your custom status filter
  };


  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      Header: () => (
        <Box data-testid="Box-kz9x" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          ID<br />&nbsp;
        </Box>
      ),
    },
    {
      accessorKey: 'user_email',
      header: 'User Email',
      Header: () => (
        <Box data-testid="Box-34b9" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          User<br />Email
        </Box>
      ),
    },
    {
      accessorKey: 'user_id',
      header: 'User Id',
      Header: () => (
        <Box data-testid="Box-ejtl" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          User<br />ID
        </Box>
      ),
    },
    {
      accessorFn: (row) => row.timestamp ? dayjs(row.timestamp).format('YYYY-MM-DD HH:mm:ss') : row.timestamp,
      header: 'Timestamp',
      Header: () => <div data-testid="div-p9j1" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
        Timestamp<br />&nbsp;
      </div>,
      Cell: ({ cell }) => (
        <div data-testid="div-f1q9" style={{ whiteSpace: 'pre-wrap' }}>
          {cell.getValue()?.toString().split(' ').join('\n')}
        </div>
      ),
    },
    {
      accessorKey: 'action',
      header: "Audit Type",
      Header: () => (
        <Box data-testid="Box-6p4k" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Audit<br />Type
        </Box>
      ),
      Cell: ({ cell }) => {
        // Get the strips from the row
        const label = (cell.getValue() as string) || "";
        const displayLabel = label ? capitalize(label) : "";

        return (
          <Box data-testid="Box-yp0i" sx={{}}>{displayLabel}</Box>
        );
      },
    },
    {
      accessorKey: 'table_name',
      header: 'Table Name',
      Header: () => (
        <Box data-testid="Box-anwf" sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '50px' }}>
          Tabel<br />Name
        </Box>
      ),
      Cell: ({ cell }) => {
        // Get the strips from the row
        const label = (cell.getValue() as string) || "";

        return (
          <Box data-testid="Box-87y2" sx={{ fontWeight: 'bold' }}>{label}</Box>
        );
      },
    },
    {
      accessorKey: "changes",
      header: "Changes",
      Header: () => (
        <Box data-testid="Box-rgq3"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
            minHeight: 50,
          }}
        >
          Changes
        </Box>
      ),
      Cell: ({ cell, row }) => {
        const changes = cell.getValue() as Record<string, [string, string]>;
        return (
          <Box data-testid={`Box-cvcu-id-${row.original.id}`}
            component="div"
            sx={{
              display: "grid",
              // 3 columns: field / old / new
              gridTemplateColumns: "130px 1fr 1fr",
              rowGap: 1,
              columnGap: 1.5,
              border: "1px solid",
              borderColor: "divider",
              p: 1,
              borderRadius: '10px'
            }}
          >
            {Object.entries(changes).map(([field, [oldVal, newVal]]) => (
              <Fragment key={field}>
                {/* 1) Field name */}
                <Box data-testid={`Box-field-id-${row.original.id}-field-${field}`}
                  sx={{
                    // fontWeight: "bold",
                    borderBottom: Object.entries(changes).length > 1 ? "1px solid" : '',
                    borderColor: "divider",
                    py: 0.5,
                  }}
                >
                  {field}:
                </Box>

                {/* 2) Old value (only on updates) */}
                {row.original.action === "update" ? (
                  <Box data-testid={`Box-oldvalue-id-${row.original.id}-field-${field}`}
                    sx={{
                      textDecoration: "line-through",
                      color: colorConfigs.buttons.cancel,
                      borderBottom: Object.entries(changes).length > 1 ? "1px solid" : '',
                      borderColor: "divider",
                      py: 0.5,
                    }}
                  >
                    {oldVal}
                  </Box>
                ) : (
                  // keep the grid cell, but empty & bordered
                  <Box data-testid={`Box-oldvalue-id-${row.original.id}-field-${field}`}
                    sx={{
                      borderBottom: Object.entries(changes).length > 1 ? "1px solid" : '',
                      borderColor: "divider",
                      // minWidth: '20px',
                      py: 0.5,
                    }}
                  />
                )}

                {/* 3) New value */}
                <Box data-testid={`Box-newvalue-id-${row.original.id}-field-${field}`}
                  sx={{
                    color: colorConfigs.tables.headBg,
                    borderBottom: Object.entries(changes).length > 1 ? "1px solid" : '',
                    borderColor: "divider",
                    py: 0.5,
                  }}
                >
                  {newVal}
                </Box>
              </Fragment>
            ))}
          </Box>
        );
      },
    }
  ];



  // Define the type for the keys for handling persistance of table settings in session storage
  type AuditSessionStorageKeyType = keyof AuditSessionStorageModel;

  // Generic handler function for persisting table parameters
  const handleParameterChange = <T,>(
    key: AuditSessionStorageKeyType,
    currentValue: T,
    setValueFunction: (value: T) => void,
    newValue: T | ((prevValue: T) => T)
  ) => {
    const updatedValue = typeof newValue === 'function' ? (newValue as (prevValue: T) => T)(currentValue) : newValue;
    AuditTableParameterStorageService.set(key, updatedValue);
    setValueFunction(updatedValue);
  };

  // Handlers for persisting table: sorting, pagination, visibility, filter, search, and search bar visibility
  const setSortingFunction = (newSortingValue: any) =>
    handleParameterChange(AuditSessionStorageKey.sorting, sorting, setSorting, newSortingValue);
  const setPaginationChangeFunction = (newPaginationValue: any) =>
    handleParameterChange(AuditSessionStorageKey.pagination, pagination, setPagination, newPaginationValue);
  const setFilterChangeFunction = (newFilterChangeValue: any) =>
    handleParameterChange(AuditSessionStorageKey.columnFilters, columnFilters, setColumnFilters, newFilterChangeValue);
  const setGlobalFilterChangeFunction = (newGlobalFilterChangeValue: string | ((prevValue: string) => string)) =>
    handleParameterChange(AuditSessionStorageKey.search, globalFilter, setGlobalFilter, newGlobalFilterChangeValue);
  const setShowGlobalFilterChangeFunction = (newShowGlobalFilterChangeValue: any) =>
    handleParameterChange(AuditSessionStorageKey.showSearch, showGlobalFilter, setShowGlobalFilter, newShowGlobalFilterChangeValue);


  return <MaterialReactTable
    columns={columns}
    data={rows}
    filterFns={filterFns}
    state={{
      isLoading,
      sorting,
      columnFilters,
      pagination,
      globalFilter,
      showGlobalFilter
    }}
    onSortingChange={setSortingFunction}
    onColumnFiltersChange={setFilterChangeFunction}
    onPaginationChange={setPaginationChangeFunction}
    onGlobalFilterChange={setGlobalFilterChangeFunction}
    onShowGlobalFilterChange={setShowGlobalFilterChangeFunction}
    defaultColumn={{
      minSize: 10,
      maxSize: 160,
      size: 10,
    }}
    enableColumnOrdering={true}
    positionActionsColumn="last"
    enableRowSelection
    selectAllMode="all"
    // enableRowActions
    initialState={{
      sorting,
      columnVisibility,
      columnFilters,
      pagination,
      globalFilter,
      density: 'compact',
      columnPinning: { left: ['mrt-row-select'], right: ['mrt-row-actions'] },
    }}

    // TABLE STYLING IS IN THE THEME
    displayColumnDefOptions={{
      'mrt-row-actions': {
        header: "Actions", // Renames Actions Column
      },
      'mrt-row-select': {
        visibleInShowHideMenu: false, //hide the built-in row actions column from the show hide menu
      },
    }}

    muiTopToolbarProps={({ table }) => ({
      sx: (theme: any) => ({
        ...((theme.components?.MuiCustomToolbar?.styleOverrides?.root) || {}),
      }),
    })}

    muiTableHeadProps={{
      sx: (theme: any) => theme.components?.MuiTableHead?.styleOverrides?.root || {},
    }}

    muiTableHeadCellProps={({ column }) => ({
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableCell?.styleOverrides?.head || {}),
        ...(column.id === 'mrt-row-select' && {
          maxWidth: '50px',
          width: '50px',
        }),
      }),
    })}

    muiFilterTextFieldProps={{
      sx: (theme: any) => theme.components?.MuiFilterTextField?.styleOverrides?.root || {},
    }}

    muiTableBodyProps={{
      sx: (theme: any) => theme.components?.MuiTableBody?.styleOverrides?.root || {},
    }}

    muiPaginationProps={{
      rowsPerPageOptions: [250, 500, 1000],
      sx: {
        padding: 0,
        margin: 0,
        '& .MuiTablePagination-root': {
          padding: 0,
          margin: 0,
        },
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          minHeight: '0px',
        },
        '& .MuiTablePagination-selectLabel': {
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          margin: 0,
        },
      },
    }}

    muiBottomToolbarProps={{
      // BOTTOM TOOLBAR (Pagination)
      sx: {
        height: '41px',
        maxHeight: '41px',
        minHeight: '41px',
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
        backgroundColor: '#fff',
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.1)',
        margin: 0,
        padding: 0,
        '& .MuiToolbar-root': {
          padding: 0,
          margin: 0,
          paddingBottom: 0,
          paddingTop: 0,
        },
        '& .MuiBox-root': {
          p: 0,
        },
        '& .MuiIconButton-root': {
          color: colorConfigs.tables.headBg,
        },
        '& .MuiTablePagination-root': {
          margin: 0,
          paddingTop: 0.5,
          paddingBottom: 0.5,
          color: colorConfigs.buttons.bg,
        },
        '& .MuiTablePagination-selectLabel': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-displayedRows': {
          color: colorConfigs.buttons.bg,
          padding: 0,
          margin: 0,
        },
        '& .MuiTablePagination-actions': {
          color: colorConfigs.tables.headBg,
          padding: 0,
          margin: 0,
        },
      },
    }}

    muiSelectAllCheckboxProps={{
      'data-testid': 'Select-all-checkbox',
    } as any}

    muiSelectCheckboxProps={({ row }) => ({
      'data-testId': `Select-row-with-id-${row.original.id}`,
    } as any)}

    muiTableBodyRowProps={({ row }) => ({
      // slap the test‑id on here
      'data-testid': `Row-with-id-${row.original.id}`,

      // your existing handlers and styles
      onClick: (event: any) => {
        console.log(row.original);
        openEditPage(row);
      },
      sx: (theme: any) => ({
        ...(theme.components?.MuiTableBodyRow?.styleOverrides?.root || {}),
        backgroundColor:
          row.index % 2 === 0
            ? colorConfigs.tables.firstRow
            : colorConfigs.tables.secondRow,
      }),
    } as any)}

    muiTableBodyCellProps={({ cell, row }) => ({
      // this creates: e.g. id="email-cell-for-row-42"
      'data-testid': `${cell.column.id}-cell-for-row-${row.original.id}`,

      // preserve any other cell styles
      sx: {},
    } as any)}


    renderToolbarInternalActions={({ table }) => (
      <Box data-testid="Box-vuhw" sx={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem' }}>
        {/* 1) Show/Hide Search */}
        <MRT_ToggleGlobalFilterButton
          table={table}
          data-testId={"Toggle-Search" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 2) Show/Hide Filters */}
        <MRT_ToggleFiltersButton
          table={table}
          data-testId={"Toggle-Filters" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 3) Show/Hide Columns */}
        <MRT_ShowHideColumnsButton
          table={table}
          data-testId={"Toggle-Columns" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}
        />

        {/* 4) Show/Hide Density */}
        <MRT_ToggleDensePaddingButton
          table={table}
          data-testId={"Toggle-Density" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />

        {/* 5) Toggle Full Screen */}
        <MRT_ToggleFullScreenButton
          table={table}
          data-testId={"Toggle-Fullscreen" as any}
          sx={{ py: 0.8, px: 1.5, border: '0.5px solid rgb(154, 148, 157)', borderRadius: '4px', boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)" }}

        />
      </Box>
    )}


    renderTopToolbarCustomActions={({ table }) => {

      const handleArchiveCycles = () => {
        table.getSelectedRowModel().flatRows.map(row => {
          //Add archive logic
        });
      };

      const handleExportAuditRows = (rows: any[]) => {
        const excelRows = [];
        const now = dayjs().format('YYYY-MM-DD HH:mm:ss');
        const filename = `${now}_AuditList`;

        for (let x = 0; x < rows.length; x++) {
          const audit = rows[x].original;

          // Build the base row with the SKU fields.
          const excelRow: any = {
            "ID": audit.id,
            "Code": audit.code,
            // Other SKU fields you might want to export:
            "Active": audit.is_active ? "Yes" : "No",
            // Add additional SKU fields as required.
          };

          // Flatten the strips: we assume audit.strips is an array and each strip has a "position" (1-indexed)
          // as well as fields "label", "threshold", and "invalid_threshold".
          // Create an array of 10 positions.
          const stripSlots = new Array(10).fill(null);
          const strips = (audit.strips as any[]) || [];

          // Assign each strip to its proper position (index = position - 1)
          strips.forEach((strip: any) => {
            const posIndex = strip.position - 1;
            if (posIndex >= 0 && posIndex < 10) {
              stripSlots[posIndex] = strip;
            }
          });

          // For each of the 10 positions, add the strip fields to the excel row.
          for (let i = 0; i < 10; i++) {
            const strip = stripSlots[i];
            // If there is a strip, export its details; otherwise, export empty strings.
            excelRow[`Strip ${i + 1} Label`] = strip ? strip.label : "";
            excelRow[`Strip ${i + 1} Threshold`] = strip ? strip.threshold : "";
            excelRow[`Strip ${i + 1} Invalid Threshold`] = strip ? strip.invalid_threshold : "";
          }

          excelRows.push(excelRow);
        }

        // Export using the tsXLXS library.
        tsXLXS().exportAsExcelFile(excelRows).saveAsExcelFile(filename);
      };




      return (

        <Box data-testid="Box-cbcb" sx={{ display: 'flex', gap: '0.3rem', height: "40px", alignItems: 'center' }}>
          <Tooltip data-testid="Tooltip-71mf" title="View action on selected data" placement="top" arrow>
            <Box data-testid="Box-w0x3">
              <RareButton data-testid="RareButton-ptqx"
                variant="outlined"
                onClick={handleClick}
                icon={<KeyboardArrowDownIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: isMobile ? 0 : 1, px: isMobile ? 1 : 2, fontSize: '14px' }}
              >
                {isMobile ? "" : "Bulk Actions"}
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-pddo" title="Refresh Data" placement="top" arrow>
            <Box data-testid="Box-9n50">
              <RareButton data-testid="RareButton-ctrs"
                variant="outlined"
                onClick={() => handleRefreshButton()}
                center={true}
                icon={<RefreshIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>
          <Tooltip data-testid="Tooltip-sodo" title="Clear All Table Filters" placement="top" arrow>
            <Box data-testid="Box-tth5">
              <RareButton data-testid="RareButton-1duc"
                variant="outlined"
                onClick={() => handleRemoveAllFilters()}
                icon={<FilterAltOffOutlinedIcon sx={{ fontSize: isMobile ? '16px' : '24.5px', p: 0, m: 0, display: 'flex', alignItems: 'center' }} />}
                sx={{ gap: 0, px: isMobile ? 1 : 2 }}
              >
              </RareButton>
            </Box>
          </Tooltip>

          <StyledMenu
            id="demo-customized-menu"
            MenuListProps={{
              'aria-labelledby': 'demo-customized-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem data-testid="MenuItem-dxl4" disabled={
              !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
            } onClick={() => handleExportAuditRows(table.getSelectedRowModel().rows)} disableRipple>
              Export to Excel
            </MenuItem>
          </StyledMenu>
        </Box>
      );
    }}

    renderRowActions={({ row, table }) => (

      <Box data-testid="Box-ym3d" sx={{ gap: '0.5rem', display: "flex" }}>
        {/* <Tooltip data-testid="Tooltip-viun"     
                    arrow
                    placement="left"
                    title="Cycles"
                    > */}
        <Badge data-testid="Badge-cgc8"
          badgeContent={row.original.total_cycles}
          color={row.original.total_cycles ? 'primary' : 'default'}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiBadge-badge': {
              top: '13px',
              right: '10px',
              background: colorConfigs.tables.headBg,
            },
          }}
          overlap="circular"
        >
          <IconButton data-testid="IconButton-q4gi"
            disabled={!row.original.total_cycles}
            component={RouterLink}
            to={`/cycles?user_id=${row.original.id}`}
            state={{ clinicID: row.original.id }}
            sx={{ transition: 'transform 0.3s ease' }}
          >
            <RotateRightOutlinedIcon fontSize="large" sx={{ color: row.original.total_cycles ? colorConfigs.sidebar.color : colorConfigs.sidebar.bg }} />
          </IconButton>
        </Badge>
        {/* </Tooltip> */}
      </Box>
    )}
  />
}

export default AuditsTable;

